import React, { useCallback, useMemo, useState } from 'react';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import { TriageFilterBar } from '@components/molecules/TriageFilterBar';
import {
  TriageFilter,
  triageFilterToTypes,
} from '@components/molecules/TriageFilterBar/TriageFilterBar.types';
import { useProjectFeedByProjects } from '@pages/projects/components/ProjectFeed/hooks';
import { usePendingIncidents } from '@hooks/api/incident/usePendingIncidents';
import { usePendingInvoicesCount, useTask } from '@hooks/api';
import { ActivityResolutionStatus } from '@models/activity.model';
import { Project } from '@models/project.model';
import { ProjectFeedWithLoading } from '../components/ProjectFeedWithLoading';
import { ProjectFilter } from '../components/ProjectFilter';
import { ClientDashboardFlags } from './clientDashboardFlags';

interface ClientFeedColumnProps {
  projects: Project[];
  flags: ClientDashboardFlags;
  onProjectClick: (projectId: string) => void;
  /** Rendered at the top of the column, inside its fixed height (the page header). */
  headerSlot?: React.ReactNode;
}

/**
 * Sprint 43, story-223. The CLIENT home's feed column. It is only ever rendered when the
 * checklist's `activity` key is ON, so every query it owns is mounted conditionally by
 * construction, never "fetch then hide". Composes the dashboard's building blocks itself
 * (`DashboardContent` hardwires the Warehouse pill and the "Needs your action" block, which a
 * client must never get): project filter without a refresh button, triage pills limited to
 * Incidents/Invoices (no Bids), and a read-only feed.
 */
export const ClientFeedColumn: React.FC<ClientFeedColumnProps> = ({
  projects,
  flags,
  onProjectClick,
  headerSlot,
}) => {
  const [selectedProject, setSelectedProject] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [triageFilter, setTriageFilter] = useState<TriageFilter>('all');
  const [isTaskDetailModal, setIsTaskDetailModal] = useState(false);
  const { fetchTask, task } = useTask();

  const allProjectIds = useMemo(
    () =>
      projects
        .map((project) => project.projectId)
        .filter((id): id is string => Boolean(id)),
    [projects],
  );
  const projectsIds = useMemo(
    () => (selectedProject ? [selectedProject.value] : allProjectIds),
    [selectedProject, allProjectIds],
  );

  const projectOptions = useMemo(
    () =>
      projects
        .filter((project) => Boolean(project.projectId))
        .map((project) => ({
          value: project.projectId as string,
          label: project.name,
        })),
    [projects],
  );

  const feedTypes = useMemo(
    () => triageFilterToTypes(triageFilter),
    [triageFilter],
  );
  // Triage pills are action queues: only unresolved items. "All Activity" keeps history.
  const feedResolutionStatus =
    triageFilter === 'all' ? undefined : ActivityResolutionStatus.ACTIVE;

  const { activities, paginationActions, totalCount } =
    useProjectFeedByProjects({
      projectsId: projectsIds,
      types: feedTypes,
      resolutionStatus: feedResolutionStatus,
      enabled: flags.showFeed,
    });

  // The server scopes both reads to the client's own projects and checklist.
  const { data: pendingIncidents = [] } = usePendingIncidents({
    enabled: flags.showIncidents,
  });
  const { data: pendingInvoicesCount = 0 } = usePendingInvoicesCount(
    flags.showInvoices ? allProjectIds : [],
  );

  const triageCounts = useMemo(
    () => ({
      incident: flags.showIncidents ? pendingIncidents.length : 0,
      budget: 0,
      invoice: flags.showInvoices ? pendingInvoicesCount : 0,
    }),
    [
      flags.showIncidents,
      flags.showInvoices,
      pendingIncidents.length,
      pendingInvoicesCount,
    ],
  );
  const showPills = flags.showIncidents || flags.showInvoices;

  const handleOpenTaskDetail = useCallback(
    (taskId: string) => {
      fetchTask(taskId);
      setIsTaskDetailModal(true);
    },
    [fetchTask],
  );

  return (
    <div className="flex flex-col gap-3 min-h-0 h-[calc(100vh-3rem)]">
      {headerSlot}
      <div className="flex flex-col flex-1 min-h-0 space-y-4">
        {projectOptions.length > 1 && (
          <ProjectFilter
            selectedProject={selectedProject}
            projectOptions={projectOptions}
            onProjectChange={setSelectedProject}
          />
        )}

        {showPills && (
          <TriageFilterBar
            activeFilter={triageFilter}
            onChange={setTriageFilter}
            counts={triageCounts}
            canInvoice={flags.showInvoices}
            hiddenPills={['budget']}
          />
        )}

        <div className="mt-6 flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <ProjectFeedWithLoading
            activities={activities}
            totalCount={totalCount}
            isLoading={paginationActions?.isLoading}
            paginationActions={paginationActions}
            handleOpenTaskDetail={handleOpenTaskDetail}
            onProjectClick={onProjectClick}
            readOnly
          />
        </div>
      </div>

      <TaskDetailModal
        task={task}
        isTaskDetailModal={isTaskDetailModal}
        setIsTaskDetailModal={setIsTaskDetailModal}
        project={
          projects.find((p) => task?.projectId === p.projectId) as Project
        }
      />
    </div>
  );
};
